/*
 * Undo a removal from the cart: the drawer's Remove, a swipe, or the cart
 * page's remove. Both carts' own code asks window.hlCartRemoving() before
 * they send a removal (assets/cart.js for the drawer, assets/hl-cart.js for
 * the page), so this needs no hold on window.fetch, which other scripts on
 * the live store may wrap or replace.
 *
 * The line as it was (variant, quantity, properties: the set-merge and
 * made-to-order notes ride along) is read fresh from /cart.js before the
 * removal goes. Once it has gone, a pill rises at the bottom, "Removed
 * Coastal 3 Seater · Undo", for six seconds (held while hovered or
 * focused). Undo adds the line back and re-draws the cart. A newer removal
 * takes its place.
 *
 * Styles: assets/hl-qty.css (.hl-undo).
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SHOW_MS = 6000;
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var canAnimate = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  var SETTLE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

  var lastCart = null;
  var realFetch = window.fetch.bind(window);

  function path(url) {
    try {
      return new URL(String(url && url.url ? url.url : url), window.location.href).pathname;
    } catch (e) {
      return '';
    }
  }

  function isCart(p) {
    return /\/cart(\.js|\.json)?$/.test(p);
  }

  function keep(cart) {
    if (cart && cart.items && typeof cart.item_count === 'number') lastCart = cart;
  }

  function loadCart() {
    return realFetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { keep(c); return c; })
      .catch(function () {});
  }

  // The line a change call names: by key (id) or by 1-based position (line).
  function lineFor(body) {
    if (!lastCart || !body) return null;
    if (body.id !== undefined && body.id !== null) {
      var id = String(body.id);
      return lastCart.items.filter(function (i) { return i.key === id || String(i.id) === id; })[0] || null;
    }
    if (body.line) return lastCart.items[parseInt(body.line, 10) - 1] || null;
    return null;
  }

  /* ---------- the row leaving the drawer ---------- */

  /*
   * In the drawer a removed row slides out to the left and fades, then the
   * list closes up over the gap; the totals roll down at once. The removal
   * goes to Shopify as soon as the line has been noted, and its answer
   * (which re-draws the drawer) is held until the row has gone, so the
   * re-draw lands on a list
   * that already looks like it. A swiped row has already slid; it just
   * closes up. If Shopify says no, the row comes back.
   */
  function leaveAnim(row) {
    return row._hlLeaving || (row._hlLeaving = leave(row));
  }

  function leave(row) {
    var fg = row.querySelector('[data-hlc-swipe-fg]') || row;
    var slid = /translateX\(-/.test(fg.style.transform || '');
    var parts = [row];
    var next = row.nextElementSibling;
    if (next && next.matches('[data-hl-set-merged]')) parts.push(next);
    var anims = [];
    if (reduceMotion || !canAnimate) {
      parts.forEach(function (el) {
        if (canAnimate) anims.push(el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, fill: 'forwards' }));
      });
    } else {
      if (!slid) {
        anims.push(fg.animate(
          [{ transform: 'none', opacity: 1 }, { transform: 'translateX(-40px)', opacity: 0 }],
          { duration: 220, easing: SETTLE, fill: 'forwards' }
        ));
      }
      parts.forEach(function (el) {
        var cs = window.getComputedStyle(el);
        el.style.overflow = 'hidden';
        anims.push(el.animate(
          [
            { height: el.offsetHeight + 'px', paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, marginTop: cs.marginTop, marginBottom: cs.marginBottom, opacity: 1 },
            { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px', opacity: 0 }
          ],
          { duration: 300, delay: slid ? 0 : 140, easing: SETTLE, fill: 'forwards' }
        ));
      });
    }
    var totalsBack = window.hlCartLineGone ? window.hlCartLineGone(row) : null;
    var done = Promise.all(anims.map(function (a) { return a.finished.catch(function () {}); }));
    return {
      done: done,
      // Shopify said no: put the row (and the totals) back.
      back: function () {
        anims.forEach(function (a) { a.cancel(); });
        parts.forEach(function (el) { el.style.overflow = ''; });
        if (fg !== row) {
          fg.style.transition = '';
          fg.style.transform = '';
          row.classList.remove('is-open');
        }
        if (totalsBack) totalsBack();
      }
    };
  }

  // The animation starts on the tap itself, before Dawn's handler runs.
  document.addEventListener('click', function (e) {
    var rb = e.target.closest && e.target.closest('cart-remove-button');
    var row = rb && rb.closest('cart-drawer') && rb.closest('.hlc-row, .cart-item');
    if (row) leaveAnim(row);
  }, true);

  // The cart's line as it is now; quick, or the last one seen if the cart is slow.
  function freshCart() {
    var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(lastCart); }, 700); });
    return Promise.race([loadCart().then(function (c) { return c || lastCart; }), timeout]);
  }

  /*
   * Called by the cart's own code just before it removes a line, with
   * { line } (1-based, the drawer) or { id } (the key, the cart page).
   * ready: wait for this before sending (the line has been noted).
   * settle(answer): call with Shopify's answer; resolves once the row's
   * leave animation has finished, so the redraw doesn't cut it short.
   */
  window.hlCartRemoving = function (which) {
    var drawerRow = which.line ? document.getElementById('CartDrawer-Item-' + which.line) : null;
    var leaving = drawerRow ? leaveAnim(drawerRow) : null;
    var noted = null;
    var ready = freshCart().then(function (cart) {
      if (cart) {
        lastCart = cart;
        noted = lineFor(which);
      }
    });
    return {
      ready: ready,
      settle: function (answer) {
        var gone = answer && answer.items && !answer.status && !answer.errors &&
          !(noted && answer.items.some(function (i) { return i.key === noted.key; }));
        keep(answer);
        var wait = leaving ? leaving.done : Promise.resolve();
        return wait.then(function () {
          if (gone && noted) offer(noted);
          if (drawerRow) {
            // Once Dawn has drawn the answer the row is gone; if it's still
            // here, Shopify said no.
            setTimeout(function () {
              if (drawerRow.isConnected) {
                drawerRow._hlLeaving = null;
                leaving.back();
              }
            }, 400);
          }
        });
      }
    };
  };

  // Keep the last full cart Shopify sent, without holding anything up.
  window.fetch = function (input, init) {
    var p = path(input);
    var call = realFetch(input, init);
    if (/\/cart\/(change|update)(\.js)?$/.test(p) || isCart(p)) {
      call.then(function (r) {
        if (r.ok) r.clone().json().then(keep, function () {});
      }, function () {});
    } else if (/\/cart\/add(\.js)?$/.test(p)) {
      // add only answers with the added lines; fetch the whole cart after.
      call.then(function () { loadCart(); }, function () {});
    }
    return call;
  };

  /* ---------- the pill ---------- */

  var pill = null;
  var hideTimer = null;
  var held = false;

  function cleanProps(p) {
    var out = {};
    if (p && typeof p === 'object') {
      Object.keys(p).forEach(function (k) {
        var v = p[k];
        if (v === null || v === undefined || typeof v === 'object') return;
        out[k] = String(v);
      });
    }
    return out;
  }

  function hide() {
    clearTimeout(hideTimer);
    if (!pill) return;
    var n = pill;
    pill = null;
    var gone = function () { if (n.parentNode) n.parentNode.removeChild(n); };
    if (canAnimate && !reduceMotion) {
      // Back down the way it came.
      n.animate([{ opacity: 1, transform: 'translate(-50%, 0)' }, { opacity: 0, transform: 'translate(-50%, 12px)' }], { duration: 180, easing: 'ease-in', fill: 'forwards' })
        .finished.then(gone, gone);
    } else {
      gone();
    }
  }

  function arm() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () { if (!held) hide(); }, SHOW_MS);
  }

  function offer(line) {
    hide();
    var n = document.createElement('div');
    n.className = 'hl-undo';
    n.setAttribute('role', 'status');
    var text = document.createElement('span');
    text.className = 'hl-undo__text';
    var name = line.product_title || line.title || 'item';
    text.textContent = 'Removed ' + (line.quantity > 1 ? line.quantity + ' \u00d7 ' : '') + name;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hl-undo__btn';
    btn.textContent = 'Undo';
    n.appendChild(text);
    n.appendChild(btn);
    document.body.appendChild(n);
    pill = n;

    if (canAnimate && !reduceMotion) {
      n.animate([{ opacity: 0, transform: 'translate(-50%, 16px) scale(0.97)' }, { opacity: 1, transform: 'translate(-50%, 0) scale(1)' }], { duration: 320, easing: SETTLE });
    }
    n.addEventListener('mouseenter', function () { held = true; clearTimeout(hideTimer); });
    n.addEventListener('mouseleave', function () { held = false; arm(); });
    n.addEventListener('focusin', function () { held = true; clearTimeout(hideTimer); });
    n.addEventListener('focusout', function () { held = false; arm(); });
    btn.addEventListener('click', function () { restore(line, n, btn, text); });
    arm();
  }

  function restore(line, n, btn, text) {
    btn.disabled = true;
    btn.textContent = 'Putting it back\u2026';
    clearTimeout(hideTimer);
    var page = document.querySelector('hl-cart');
    var body = { items: [{ id: line.variant_id || line.id, quantity: line.quantity, properties: cleanProps(line.properties) }] };
    if (!page && window.hlCartSections) body = window.hlCartSections(body);
    realFetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok || j.status) throw j;
          return j;
        });
      })
      .then(function (res) {
        held = false;
        if (pill === n) hide();
        loadCart();
        if (page && page.refresh) {
          page.refresh();
          realFetch(ROOT + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            var pieces = window.hlCartPieces ? window.hlCartPieces(cart) : cart.item_count;
            Array.prototype.forEach.call(document.querySelectorAll('.hl-header__cart-count'), function (el) {
              el.textContent = pieces;
            });
          }).catch(function () {});
        } else if (window.hlCartRedraw) {
          window.hlCartRedraw(res);
        } else {
          window.location.reload();
        }
      })
      .catch(function (err) {
        if (window.console && err) console.warn('[hl-cart-undo]', err);
        btn.disabled = false;
        btn.textContent = 'Try again';
        text.textContent = (err && err.description) || 'That didn\u2019t go back in.';
        arm();
      });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && pill && pill.contains(document.activeElement)) hide();
  });

  loadCart();
})();
